import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_awesome-napier-3/artifacts/lklioiyf_xteam%20lighted%20building%20sign.jpg";

// Social media icons
const FACEBOOK_ICON = "https://customer-assets.emergentagent.com/job_awesome-napier-3/artifacts/z6amme12_icn-facebook.png";
const INSTAGRAM_ICON = "https://customer-assets.emergentagent.com/job_awesome-napier-3/artifacts/6yu4bugf_icn-instagram.png";
const YOUTUBE_ICON = "https://customer-assets.emergentagent.com/job_awesome-napier-3/artifacts/5uymobq4_icn-youtube.png";

// Social media URLs
const FACEBOOK_URL = "https://www.facebook.com/xteamfitness/";
const INSTAGRAM_URL = "https://www.instagram.com/xteamfitness/";
const YOUTUBE_URL = "https://www.youtube.com/channel/UCFupa9cKZeJlSUNAhVVAtyg";

export default function Footer() {
  return (
    <footer data-testid="main-footer">
      {/* Main Footer Section - #323232 */}
      <div style={{ backgroundColor: '#323232' }}>
        <div className="py-16" style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <Link to="/" className="inline-block mb-6">
                <img 
                  src={FOOTER_LOGO_URL} 
                  alt="X-Team Fitness" 
                  style={{ width: '225px', height: 'auto' }}
                  loading="lazy"
                />
              </Link>
              <p className="text-white mb-6 leading-relaxed">
                Richmond's indoor/outdoor coaching-based fitness community for adults who want expert coaching, measurable progress, accountability, and workouts that never get stale.
              </p>
            </div>

            {/* Navigation - Quick Links */}
            <div>
              <h4 className="font-heading text-2xl mb-6 tracking-wide text-white">Quick Links</h4>
              <nav className="space-y-3" aria-label="Footer navigation">
                <Link to="/programs" className="block text-white hover:text-[#FF5A3C] transition-colors">Programs</Link>
                <Link to="/hyrox" className="block text-white hover:text-[#FF5A3C] transition-colors">HYROX Training</Link>
                <Link to="/schedule" className="block text-white hover:text-[#FF5A3C] transition-colors">Schedule</Link>
                <Link to="/join" className="block text-white hover:text-[#FF5A3C] transition-colors">Join</Link>
                <Link to="/about" className="block text-white hover:text-[#FF5A3C] transition-colors">About</Link>
                <Link to="/contact" className="block text-white hover:text-[#FF5A3C] transition-colors">Contact</Link>
              </nav>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-heading text-2xl mb-6 tracking-wide text-white">Contact</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FF5A3C] mt-0.5 flex-shrink-0" />
                  <p className="text-white">
                    2928 West Leigh Street<br />
                    Richmond, VA 23230
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#FF5A3C] flex-shrink-0" />
                  <a href="tel:804-920-8086" className="text-white hover:text-[#FF5A3C] transition-colors">
                    804-920-8086
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#FF5A3C] flex-shrink-0" />
                  <a href="mailto:info@xteamfitness.com" className="text-white hover:text-[#FF5A3C] transition-colors">
                    info@xteamfitness.com
                  </a>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div>
              <h4 className="font-heading text-2xl mb-6 tracking-wide text-white">Get Started</h4>
              <p className="text-white mb-6">
                Ready to transform your fitness journey? Book your free intro today.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="footer-cta-btn"
                  className="w-full bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest py-4 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Book Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright Bar - #000000 */}
      <div style={{ backgroundColor: '#000000' }}>
        <div className="py-4" style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 2rem' }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright - Left */}
            <p className="text-gray-500 text-sm">
              &copy; Copyright {new Date().getFullYear()}. X-Team Fitness LLC. All Rights Reserved.
            </p>
            
            {/* Policies - Center */}
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
            
            {/* Social Media - Right */}
            <div className="flex items-center gap-4">
              <span className="text-gray-500 text-sm" id="social-label">Follow us</span>
              <div className="flex items-center gap-3" role="list" aria-labelledby="social-label">
                <a 
                  href={FACEBOOK_URL}
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img src={FACEBOOK_ICON} alt="Facebook" className="w-7 h-7" loading="lazy" />
                </a>
                <a 
                  href={INSTAGRAM_URL}
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img src={INSTAGRAM_ICON} alt="Instagram" className="w-7 h-7" loading="lazy" />
                </a>
                <a 
                  href={YOUTUBE_URL}
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-youtube"
                  aria-label="YouTube"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img src={YOUTUBE_ICON} alt="YouTube" className="w-7 h-7" loading="lazy" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
